// UserPanel.tsx
import React, { useState, useCallback, useLayoutEffect, Suspense, useEffect, useRef } from 'react';
import { Layout, Button, Typography, Spin, Input, Select, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
// import { LogoutOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserPanel } from '../../contexts/UserPanelContext';
import { useAuthStore } from '../../shared/hooks/useAuth';
import { useAdminSelector } from '../../contexts/AdminSelectorContext';
import Sidebar from '../../components/Sidebar/Sidebar';
// Removed RequestsPanelContainer - now using RequestsSection as a route
import { getTokenData, safeToString } from './utils';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useSocket } from '../../contexts/SocketContext';
import { useUserStore, usePanelsStore } from '../../shared/stores/useStores';
import ReactDOM from 'react-dom';
import { settingsPages } from '../Settings/Settings';
import { analyticsPages } from '../analytics/Analytics';
import { gamificationPages } from '../Gamification/Gamification';
import type { RequestsSectionRef } from './RequestsSection';
import type { UsersTableRef } from '../Users/UsersTable';
import {
  FileTextOutlined,
  AppstoreOutlined,
  TeamOutlined,
  WalletOutlined,
  FileAddOutlined,
  SearchOutlined,
  LinkOutlined,
  UserAddOutlined,
  SyncOutlined,
  AuditOutlined
} from '@ant-design/icons';

// Define single-page sections
const operationsPage = {
  key: 'operations',
  label: 'Operations',
  icon: <AppstoreOutlined />,
  color: '#1890ff',
  description: 'View and manage panel operations'
};

const requestsPage = {
  key: 'requests',
  label: 'Requests',
  icon: <FileTextOutlined />,
  color: '#52c41a',
  description: 'Manage load and withdraw requests'
};

const usersPage = {
  key: 'users',
  label: 'Users',
  icon: <TeamOutlined />,
  color: '#fa8c16',
  description: 'User management and financial hub'
};

const walletPage = {
  key: 'wallet',
  label: 'Wallet',
  icon: <WalletOutlined />,
  color: '#722ed1',
  description: 'Transfer history and wallet management'
};

const auditPage = {
  key: 'audit',
  label: 'Audit',
  icon: <AuditOutlined />,
  color: '#eb2f96',
  description: 'Request audit trail and logs'
};
import { useLocation } from 'react-router-dom';
import { App as AntdApp } from 'antd';

import { getAndSanitize } from '../../shared/utils/sanitization';

const { Text, Title } = Typography;

// Device size types
type DeviceSize = 'mobile' | 'smallTablet' | 'tablet' | 'smallDesktop' | 'desktop' | 'largeDesktop';

const { Content } = Layout;

// TV Shutdown Animation Component
const TVShutdownAnimation: React.FC<{ onAnimationComplete: () => void }> = ({ onAnimationComplete }) => {

  useEffect(() => {
    // After animations complete, call the callback
    const timer = setTimeout(() => {
      onAnimationComplete();
    }, 1800); // Aumentado aún más para asegurar transición suave

    return () => clearTimeout(timer);
  }, [onAnimationComplete]);

  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.5, delay: 0.5 } }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#000',
        zIndex: 10000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* TV Scan lines */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `repeating-linear-gradient(
            0deg,
            rgba(255, 255, 255, 0.03) 0px,
            rgba(255, 255, 255, 0.03) 1px,
            transparent 1px,
            transparent 2px
          )`,
          zIndex: 1,
          opacity: 0.8,
        }}
        animate={{
          opacity: [0.8, 0.4, 0],
        }}
        transition={{
          duration: 0.7,
          ease: "easeIn",
        }}
      />

      {/* CRT effect - red horizontal line that collapses */}
      <motion.div
        initial={{ scaleX: 1, scaleY: 1 }}
        animate={{ scaleX: 0.01, scaleY: 0.1 }}
        transition={{ 
          scaleX: {
            delay: 0.4, 
            duration: 0.5,
            ease: [0.4, 0, 0.2, 1]  
          },
          scaleY: {
            delay: 0.7,
            duration: 0.3
          }
        }}
        style={{
          width: '100%',
          height: '4px',
          backgroundColor: '#ac4949', // Cambiado a rojo
          position: 'absolute',
          boxShadow: '0 0 10px rgba(172, 73, 73, 0.8), 0 0 20px rgba(172, 73, 73, 0.5)', // Sombra roja
          zIndex: 2,
        }}
      />

      {/* CRT Flicker */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          zIndex: 3,
        }}
        animate={{
          opacity: [0, 0.5, 0, 0.2, 0]
        }}
        transition={{
          duration: 0.5,
          times: [0, 0.2, 0.5, 0.7, 1],
          ease: "easeOut",
        }}
      />
    </motion.div>,
    document.body
  );
};

export const UserPanel: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore(state => state.logout);
  const { panelLoginStatus } = useUserPanel();
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('largeDesktop');
  const [awaitingAccess, setAwaitingAccess] = useState<boolean>(false);
  const { theme, toggleTheme } = useTheme();
  const { socket, isConnected } = useSocket();
  const { isSuperAdmin, admins, isLoading: adminsLoading, selectedAdminId, setSelectedAdmin } = useAdminSelector();

  // Use Zustand stores
  const { user, fetchUser, clearUser } = useUserStore();
  const { panels, fetchPanels } = usePanelsStore();
  const [showLogoutAnimation, setShowLogoutAnimation] = useState(false);
  const { message: contextMessage } = AntdApp.useApp();

  // Analytics state
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState<string>("users");
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);

  // Settings state
  const [activeSettingsTab, setActiveSettingsTab] = useState<string>("user");

  // Gamification state
  const [activeGamificationTab, setActiveGamificationTab] = useState<string>("players");

  // Ref for RequestsSection to control modal from header
  const requestsSectionRef = useRef<RequestsSectionRef>(null);

  // Ref for UsersTable to control create user modal from header
  const usersTableRef = useRef<UsersTableRef>(null);

  // Operations search state
  const [operationsSearchParams, setOperationsSearchParams] = useState<{
    username: string;
    type?: 'DEPOSIT' | 'WITHDRAW';
    linkedTransferId: string;
  }>({
    username: '',
    type: undefined,
    linkedTransferId: '',
  });

  // Handler to update operations search from header controls
  const handleOperationsSearchChange = useCallback((field: 'username' | 'type' | 'linkedTransferId', value: string | 'DEPOSIT' | 'WITHDRAW' | undefined) => {
    setOperationsSearchParams(prev => {
      const newParams = {
        ...prev,
        [field]: value
      };
      return newParams;
    });
  }, []);

  // Wallet search state
  const [walletSearchParams, setWalletSearchParams] = useState<{
    searchTerm: string;
    filterPlatform: string | null;
  }>({
    searchTerm: '',
    filterPlatform: null,
  });
  const [walletPlatforms, setWalletPlatforms] = useState<string[]>([]);
  const [walletRefreshTrigger, setWalletRefreshTrigger] = useState(0);

  // Handlers for wallet controls
  const handleWalletSearchChange = useCallback((value: string) => {
    setWalletSearchParams(prev => ({ ...prev, searchTerm: value }));
  }, []);

  const handleWalletPlatformFilter = useCallback((platform: string | null) => {
    setWalletSearchParams(prev => ({
      ...prev,
      filterPlatform: prev.filterPlatform === platform ? null : platform
    }));
  }, []);

  const handleWalletRefresh = useCallback(() => {
    setWalletRefreshTrigger(prev => prev + 1);
  }, []);

  // Check if we're in special routes
  const isSettingsRoute = location.pathname.includes('/settings');
  const isAnalyticsRoute = location.pathname.includes('/analytics');
  const isAuditRoute = location.pathname.includes('/audit');
  const isGamificationRoute = location.pathname.includes('/gamification');
  const isOperationsRoute = location.pathname.includes('/operations');
  const isRequestsRoute = location.pathname.includes('/requests');
  const isValidationRoute = /\/requests\/[^/]+\/(validate|withdraw)/.test(location.pathname);
  const isUsersRoute = location.pathname.includes('/users');
  const isWalletRoute = location.pathname.includes('/wallet');

  // Get current section info for header
  const getCurrentSectionInfo = () => {
    if (isSettingsRoute) {
      const section = settingsPages.find(s => s.key === activeSettingsTab);
      return section;
    }
    if (isAnalyticsRoute) {
      const section = analyticsPages.find(s => s.key === activeAnalyticsTab);
      return section;
    }
    if (isAuditRoute) {
      return auditPage;
    }
    if (isGamificationRoute) {
      const section = gamificationPages.find(s => s.key === activeGamificationTab);
      return section;
    }
    if (isOperationsRoute) {
      return operationsPage;
    }
    if (isRequestsRoute) {
      return requestsPage;
    }
    if (isUsersRoute) {
      return usersPage;
    }
    if (isWalletRoute) {
      return walletPage;
    }
    return null;
  };

  const currentSection = getCurrentSectionInfo();

  // Effect for detecting window size changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setDeviceSize('mobile');
      } else if (width < 768) {
        setDeviceSize('smallTablet');
      } else if (width < 1024) {
        setDeviceSize('tablet');
      } else if (width < 1280) {
        setDeviceSize('smallDesktop');
      } else if (width < 1580) {
        setDeviceSize('desktop');
      } else {
        setDeviceSize('largeDesktop');
      }
    };

    handleResize(); // Initialize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = deviceSize === 'mobile';
  const isSmallTablet = deviceSize === 'smallTablet';
  const isTablet = deviceSize === 'tablet';
  const isSmallDesktop = deviceSize === 'smallDesktop';
  const isDesktop = deviceSize === 'desktop';

  // Determine whether to show mobile menu
  const showMobileMenu = isMobile || isSmallTablet;

  // Effect to load panels only once when the component is mounted
  useEffect(() => {
    fetchPanels().catch(error => {
      console.error('Error loading available panels:', error);
      contextMessage.error(t('navBar.errorLoadingPanels', 'Error al cargar paneles disponibles'));
    });
  }, [fetchPanels, t, contextMessage]);

  // Intercept axios errors globally for this component
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        // Central error handling for 401 and token expiration is in App.tsx
        // Only leave specific code needed for this component
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, logout, t]);

  // Fetch user data
  const fetchUserData = useCallback(async () => {
    const tokenData = getTokenData();
    if (!tokenData) {
      navigate('/');
      return;
    }

    const currentRole = getAndSanitize('role');
    
    try {
      const userData = await fetchUser(tokenData.userId);
      
      if (userData) {
        // Check awaiting access status only for USER role
        if (currentRole === 'USER') {
          setAwaitingAccess(userData.awaitingAccess === true);
        } else {
          // Make sure it's explicitly false for other roles
          setAwaitingAccess(false);
        }
      }
    } catch (error) {
      console.error('[Auth] Failed to load user:', error);
      contextMessage.error(t('common.failedToLoadUserData', 'Failed to load user data'));
    }
  }, [navigate, fetchUser, t]);

  // Load user data on component mount
  useLayoutEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Listen for socket events related to access
  useEffect(() => {
    if (!socket || !isConnected || !awaitingAccess) return;
    
    const handleAccessGranted = (data: { userId: string }) => {
      const currentUserId = getAndSanitize('userId');
      
      // If this event is for the current user
      if (currentUserId && data.userId === currentUserId) {
        
        // Update state to show dashboard
        setAwaitingAccess(false);
        
        // Show success message
        contextMessage.success(t('navBar.accessGranted', 'Acceso concedido. Bienvenido al sistema.'));
        
        // Refresh user data to get updated permissions
        fetchUserData();
      }
    };
    
    // Register event listener
    socket.on('accessGranted', handleAccessGranted);
    
    return () => {
      socket.off('accessGranted', handleAccessGranted);
    };
  }, [socket, isConnected, awaitingAccess, fetchUserData, t]);

  // Handle logout
  const handleLogout = useCallback(() => {
    
    // Show TV shutdown animation
    setShowLogoutAnimation(true);
  }, []);

  // Handle animation complete
  const handleAnimationComplete = useCallback(() => {
    // Realizar logout y limpieza
    logout();
    clearUser();
    
    // Técnica para asegurar transición fluida:
    // 1. Navegar a la página de login
    navigate('/');
    
    // 2. Mantener la animación visible un poco más para evitar ver el UI anterior
    // La animación continuará visible durante la transición y
    // solo la ocultamos después de que se complete la navegación
    setTimeout(() => {
      setShowLogoutAnimation(false);
    }, 500);
  }, [logout, navigate, clearUser]);

  // Copy to clipboard utility
  const copyToClipboard = useCallback((text: unknown) => {
    navigator.clipboard.writeText(safeToString(text));
    contextMessage.success('Copied to clipboard');
  }, [contextMessage]);

  // Create dynamic user context menu
  /* COMMENTED OUT - NOT CURRENTLY USED
  const userContextMenuItems: UserContextMenuItem[] = React.useMemo(() => {
    const baseItems: UserContextMenuItem[] = [
      {
        key: 'panelStatus',
        icon: panelLoginStatus ?
          <CloudOutlined style={{ color: '#52c41a', fontSize: '16px' }} /> :
          <DisconnectOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />,
        label: <span style={{ fontWeight: 500 }}>
          {t('navBar.panelStatus')}: <span style={{
            color: panelLoginStatus ? '#52c41a' : '#ff4d4f',
            fontWeight: 600
          }}>
            {panelLoginStatus ? t('navBar.status.connected') : t('navBar.status.disconnected')}
          </span>
        </span>,
        disabled: true
      },
      {
        key: 'copyId',
        icon: <CopyOutlined style={{ color: '#1890ff', fontSize: '16px' }} />,
        label: <span>{t('navBar.copyId')}</span>,
        onClick: () => {
          if (user?.id) {
            copyToClipboard(user.id);
            contextMessage.success(`${t('navBar.copyId')} - ${user.id}`);
          }
        }
      },
      {
        type: 'divider',
        key: 'divider1'
      },
    ];

    // On mobile and tablet, show less information to keep the menu compact
    if (showMobileMenu) {
      return [
        ...baseItems,
        {
          key: 'username',
          label: <span style={{ fontWeight: 500 }}>
            {t('navBar.username')}: <span style={{ color: '#1890ff' }}>{user?.username || 'N/A'}</span>
          </span>,
          disabled: true
        },
        {
          key: 'logout',
          icon: <LogoutOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />,
          label: <span style={{ color: '#ff4d4f', fontWeight: 500 }}>{t('navBar.logout')}</span>,
          onClick: handleLogout
        }
      ];
    }

    // On desktop, show all information
    return [
      ...baseItems,
      {
        key: 'panels',
        label: <div>
          <div style={{ fontWeight: 500, marginBottom: '8px' }}>{t('navBar.panels')}:</div>
          <PanelSquares
            panels={panels}
            onCopyId={(id) => {
              copyToClipboard(id);
              contextMessage.success(`${t('navBar.idCopied', 'ID copiado')}: ${id}`);
            }}
          />
        </div>,
        disabled: true
      },
      {
        key: 'wallets',
        label: <div>
          <div style={{ fontWeight: 500, marginBottom: '8px' }}>{t('navBar.wallets')}:</div>
          <WalletSquares
            wallets={user?.wallets || []}
            onCopyId={(id) => {
              copyToClipboard(id);
              contextMessage.success(`${t('navBar.walletIdCopied', 'ID de billetera copiado')}: ${id}`);
            }}
          />
        </div>,
        disabled: true
      },
      {
        type: 'divider',
        key: 'divider2'
      },
      {
        key: 'logout',
        icon: <LogoutOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />,
        label: <span style={{ color: '#ff4d4f', fontWeight: 500 }}>{t('navBar.logout')}</span>,
        onClick: handleLogout
      }
    ];
  }, [panelLoginStatus, user, panels, copyToClipboard, handleLogout, t, showMobileMenu]);
  */

  // Adjust header height based on device
  // const headerHeight = showMobileMenu ? 56 : 64;
  
  // Adjust padding based on device
  const getContentPadding = () => {
    if (isMobile) return '16px 12px';
    if (isSmallTablet || isTablet) return '20px 16px';
    if (isSmallDesktop) return '22px 20px';
    if (isDesktop) return '23px 22px';
    return '24px';
  };

  // Render awaiting access screen
  const renderAwaitingAccessScreen = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '2rem',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme === 'dark' ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.85)',
      zIndex: 1000
    }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        style={{
          background: theme === 'dark' ? 'rgba(20, 20, 20, 0.85)' : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          padding: '2.5rem',
          borderRadius: '12px',
          boxShadow: theme === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}
      >
        <img 
          src={
            theme === 'dark' 
              ? (showMobileMenu ? "/medium-logo-dark.png" : "/logo_green.png")
              : (showMobileMenu ? "/medium-logo.png" : "/logo_green_black.png")
          }
          alt="Logo" 
          style={{ height: 80, maxWidth: '80%', marginBottom: '1.5rem' }}
        />

        <Title level={3} style={{ color: theme === 'dark' ? '#ffffff' : '#333333' }}>
          {t('navBar.waitingForAccess', 'Validando tu sesión')}
        </Title>
        <Spin style={{ marginBottom: '1.5rem' }} />
        <Text style={{ color: theme === 'dark' ? '#999999' : '#666666', display: 'block', marginBottom: '1.5rem' }}>
          {t('navBar.accountPendingApproval', 'Tu cuenta está pendiente de aprobación. Por favor, contacta al administrador.')}
        </Text>
        <Button 
          type="primary" 
          onClick={handleLogout}
          style={{ backgroundColor: '#ac4949', borderColor: '#ac4949' }}
        >
          {t('navBar.backToLogin', 'Volver al inicio de sesión')}
        </Button>
      </motion.div>
    </div>
  );

  // Main layout with conditional overlay
  return (
    <>
      {/* Global styles for square design */}
      <style>
        {`
          .ant-menu-inline .ant-menu-item,
          .ant-menu-inline .ant-menu-submenu-title {
            border-radius: 0 !important;
          }
          .ant-menu-item-selected {
            border-radius: 0 !important;
          }
          .ant-select-selector {
            border-radius: 0 !important;
          }
          .ant-btn {
            border-radius: 0 !important;
          }
          .ant-avatar {
            border-radius: 0 !important;
          }
          .ant-badge {
            border-radius: 0 !important;
          }
        `}
      </style>
      <Layout
        className="glassmorphism-bg"
        style={{
          height: '100vh',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
      {!showLogoutAnimation && (
        <React.Fragment>
          {/* Sidebar Navigation - hidden on validation page */}
          {!isValidationRoute && <Sidebar
            theme={theme}
            toggleTheme={toggleTheme}
            isSuperAdmin={isSuperAdmin}
            admins={admins}
            adminsLoading={adminsLoading}
            selectedAdminId={selectedAdminId}
            setSelectedAdmin={setSelectedAdmin}
            panels={panels}
            user={user}
            panelLoginStatus={panelLoginStatus}
            onLogout={handleLogout}
            onCopyToClipboard={copyToClipboard}
            activeAnalyticsTab={activeAnalyticsTab}
            onAnalyticsTabChange={setActiveAnalyticsTab}
            filtersExpanded={filtersExpanded}
            onToggleFilters={() => setFiltersExpanded(!filtersExpanded)}
            activeSettingsTab={activeSettingsTab}
            onSettingsTabChange={setActiveSettingsTab}
            activeGamificationTab={activeGamificationTab}
            onGamificationTabChange={setActiveGamificationTab}
          />}

          {/* Top Header Bar Extension - Visual extension of sidebar (inverted L shape) */}
          {!isValidationRoute && (isSettingsRoute || isAnalyticsRoute || isAuditRoute || isGamificationRoute || isOperationsRoute || isRequestsRoute || isUsersRoute || isWalletRoute) && currentSection && (
            <>
              {/* Main Section Header Bar */}
              <div
                style={{
                  position: 'fixed',
                  left: '240px', // Starts where sidebar ends
                  top: 0,
                  right: 0,
                  height: '70px',
                  background: theme === 'dark' ? '#0a0a0a' : '#ffffff',
                  borderBottom: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 32px',
                  zIndex: 1000,
                  boxShadow: theme === 'dark'
                    ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: `linear-gradient(135deg, ${currentSection.color}20, ${currentSection.color}10)`,
                        border: `1px solid ${currentSection.color}40`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: currentSection.color,
                      }}
                    >
                      {currentSection.icon}
                    </div>
                    <div>
                      <Typography.Title
                        level={4}
                        style={{
                          margin: 0,
                          color: theme === 'dark' ? '#e2e8f0' : '#1e293b',
                          fontWeight: 600,
                          fontSize: '18px',
                        }}
                      >
                        {currentSection.label}
                      </Typography.Title>
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: '13px',
                          color: theme === 'dark' ? '#94a3b8' : '#64748b',
                        }}
                      >
                        {currentSection.description}
                      </Typography.Text>
                    </div>
                  </div>

                  {/* New Load Request button - Only for Requests section (not validation page) */}
                  {isRequestsRoute && !isValidationRoute && (
                    <Button
                      type="primary"
                      icon={<FileAddOutlined />}
                      onClick={() => requestsSectionRef.current?.openCreateLoadRequestModal()}
                      style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        height: '36px',
                        fontWeight: '500',
                        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                      }}
                    >
                      New Load Request
                    </Button>
                  )}

                  {/* Search controls - Only for Operations section */}
                  {isOperationsRoute && (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <Input
                        placeholder={t('operations.searchByUsername')}
                        prefix={<SearchOutlined />}
                        onChange={e => handleOperationsSearchChange('username', e.target.value)}
                        value={operationsSearchParams.username}
                        allowClear
                        onClear={() => handleOperationsSearchChange('username', '')}
                        style={{ width: '200px' }}
                      />

                      <Select
                        placeholder={t('operations.filterByType')}
                        allowClear
                        value={operationsSearchParams.type}
                        onChange={(value: 'DEPOSIT' | 'WITHDRAW' | undefined) => handleOperationsSearchChange('type', value)}
                        options={[
                          { value: 'DEPOSIT', label: 'Deposit' },
                          { value: 'WITHDRAW', label: 'Withdraw' }
                        ]}
                        style={{ width: '150px' }}
                      />

                      <Input
                        placeholder={t('operations.searchByTransfer')}
                        prefix={<LinkOutlined />}
                        onChange={e => handleOperationsSearchChange('linkedTransferId', e.target.value)}
                        value={operationsSearchParams.linkedTransferId}
                        allowClear
                        onClear={() => handleOperationsSearchChange('linkedTransferId', '')}
                        type="number"
                        style={{ width: '180px' }}
                      />
                    </div>
                  )}

                  {/* Create User button - Only for Users section */}
                  {isUsersRoute && (
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      onClick={() => usersTableRef.current?.openCreateUserModal()}
                      style={{
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        height: '36px',
                        fontWeight: '500',
                        boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)',
                      }}
                    >
                      Create User
                    </Button>
                  )}

                  {/* Wallet controls - Only for Wallet section */}
                  {isWalletRoute && (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <Input
                        placeholder="Search transfers..."
                        prefix={<SearchOutlined />}
                        onChange={e => handleWalletSearchChange(e.target.value)}
                        value={walletSearchParams.searchTerm}
                        allowClear
                        style={{ width: '200px' }}
                      />

                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {walletPlatforms.slice(0, 5).map(platform => (
                          <Tag
                            key={platform}
                            color={walletSearchParams.filterPlatform === platform ? 'blue' : 'default'}
                            onClick={() => handleWalletPlatformFilter(platform)}
                            style={{ cursor: 'pointer', margin: 0 }}
                          >
                            {platform}
                          </Tag>
                        ))}
                        {walletPlatforms.length > 5 && (
                          <Tag color="default" style={{ margin: 0 }}>
                            +{walletPlatforms.length - 5}
                          </Tag>
                        )}
                      </div>

                      <Button
                        type="primary"
                        icon={<SyncOutlined />}
                        onClick={handleWalletRefresh}
                        style={{
                          borderRadius: '8px',
                          height: '36px',
                        }}
                      >
                        Sync
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Main Content Area */}
          <Content
            className="user-panel-content"
            style={{
              marginLeft: isValidationRoute ? 0 : 240,
              marginTop: isValidationRoute ? 0 : ((isSettingsRoute || isAnalyticsRoute || isAuditRoute || isGamificationRoute || isOperationsRoute || isRequestsRoute || isUsersRoute || isWalletRoute) ? '70px' : 0),
              padding: (isValidationRoute || isSettingsRoute || isAuditRoute || isGamificationRoute || isOperationsRoute || isRequestsRoute || isUsersRoute || isWalletRoute) ? 0 : getContentPadding(),
              height: isValidationRoute ? '100vh' : ((isSettingsRoute || isAnalyticsRoute || isAuditRoute || isGamificationRoute || isOperationsRoute || isRequestsRoute || isUsersRoute || isWalletRoute) ? 'calc(100vh - 70px)' : '100vh'),
              position: 'relative',
              overflow: isValidationRoute ? 'hidden' : 'auto',
              overflowX: 'hidden',
              zIndex: 2,
              background: (isValidationRoute || isSettingsRoute || isGamificationRoute || isOperationsRoute || isRequestsRoute || isUsersRoute || isWalletRoute) ? (theme === 'dark' ? '#0a0a0a' : '#ffffff') : undefined
            }}
          >
            {!awaitingAccess ? (
              <Suspense fallback={<div>Loading...</div>}>
                <Outlet context={{
                  activeAnalyticsTab,
                  onAnalyticsTabChange: setActiveAnalyticsTab,
                  filtersExpanded,
                  onToggleFilters: () => setFiltersExpanded(!filtersExpanded),
                  activeSettingsTab,
                  onSettingsTabChange: setActiveSettingsTab,
                  activeGamificationTab,
                  onGamificationTabChange: setActiveGamificationTab,
                  requestsSectionRef,
                  operationsSearchParams,
                  usersTableRef,
                  walletSearchParams,
                  walletRefreshTrigger,
                  onWalletPlatformsUpdate: setWalletPlatforms
                }} />
              </Suspense>
            ) : null}
          </Content>

          {/* Show awaiting access overlay if needed */}
          {awaitingAccess && renderAwaitingAccessScreen()}
        </React.Fragment>
      )}

      {/* TV Shutdown Animation */}
      <AnimatePresence>
        {showLogoutAnimation && (
          <TVShutdownAnimation onAnimationComplete={handleAnimationComplete} />
        )}
      </AnimatePresence>
      </Layout>
    </>
  );
};

export default React.memo(UserPanel);